import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AnalyticsConsent } from "@/components/AnalyticsConsent";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import MobileBootstrap from "@/components/MobileBootstrap";
import { ThemeProvider } from "@/context/ThemeContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import { getHostClass, urlOnHost } from "@/lib/host";

// Shared
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminPortal from "@/pages/admin/AdminPortal";
import AcceptInvite from "@/pages/admin/AcceptInvite";
import { INTERNAL_ROLES } from "@/lib/team";
import Login from "@/pages/Login";
import PhoneLogin from "@/pages/PhoneLogin";
import Register from "@/pages/Register";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import AgreementView from "@/pages/legal/AgreementView";
import AccountLegal from "@/pages/legal/AccountLegal";
import Blog from "@/pages/Blog";
import BlogLaunchPost from "@/pages/blog/IntroducingStarterAndGrowth";
import PublicFAQ from "@/pages/FAQ";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

// Shopper (customer app)
import Home from "@/pages/Home";
import Coupons from "@/pages/Coupons";
import CouponDetail from "@/pages/CouponDetail";
import Stores from "@/pages/Stores";
import StoreDetail from "@/pages/StoreDetail";
import StorefrontDetail from "@/pages/StorefrontDetail";
import BrandsFeed from "@/components/BrandsFeed";
import Categories from "@/pages/Categories";
import UserDashboard from "@/pages/user/Dashboard";
import UserWallet from "@/pages/user/Wallet";
import AffiliateRedirect from "@/pages/affiliate/Redirect";
import GoRedirect from "@/pages/GoRedirect";
import AffiliatePortal from "@/pages/affiliate/AffiliatePortal";

// Reseller (resellers.cyannect.com)
import ResellerPortal from "@/pages/reseller/ResellerPortal";

// Merchant (main marketing site)
import MerchantHome from "@/pages/MerchantHome";
import ShoppersLanding from "@/pages/ShoppersLanding";
import ShortLinkRedirect from "@/pages/ShortLinkRedirect";
import MerchantPortal from "@/pages/merchant/MerchantPortal";
import Pricing from "@/pages/Pricing";
import Demo from "@/pages/Demo";
import DemoCancel from "@/pages/DemoCancel";
import WhyCyannect from "@/pages/WhyCyannect";
import About from "@/pages/About";
import ApiDocs from "@/pages/ApiDocs";
import WebSdkDocs from "@/pages/WebSdkDocs";
import ConversionVerificationDocs from "@/pages/docs/ConversionVerificationDocs";
import ReferralRedirect from "@/pages/ReferralRedirect";
import ShopperCashback from "@/pages/shopper/Cashback";
import RewardsWallet from "@/pages/shopper/RewardsWallet";
import MerchantIntegrations from "@/pages/merchant/Integrations";
import ComingSoon from "@/pages/ComingSoon";
import SupportChatWidget from "@/components/SupportChatWidget";
import ScrollToTop from "@/components/ScrollToTop";

// Flag to toggle Coming Soon mode across the root home page
const isComingSoon = false;

function Search() { return <Coupons />; }
function AppShell({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1">{children}</div>
      <Footer />
    </div>
  );
}

/** Cross-host redirect — sends the visitor to the correct subdomain. */
function CrossHost({ to, path }) {
  if (typeof window !== "undefined") {
    window.location.replace(urlOnHost(to, path));
  }
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
      Redirecting to the {to === "app" ? "customer app" : to === "main" ? "merchant site" : "admin portal"}…
    </div>
  );
}

/** Cross-host redirect that keeps the slug / id of a deep link. */
function CrossHostParam({ to, base }) {
  const { slug, id } = useParams();
  return <CrossHost to={to} path={`${base}/${encodeURIComponent(slug || id)}`} />;
}

/** Routes for admin.cyannect.com — nothing but admin sign-in + portal. */
function AdminRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<AdminLogin />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/accept-invite" element={<AcceptInvite />} />
      <Route path="/admin/*" element={
        <ProtectedRoute roles={INTERNAL_ROLES}>
          <AdminPortal />
        </ProtectedRoute>
      } />
      <Route path="*" element={<AdminLogin />} />
    </Routes>
  );
}

/** Routes for app.cyannect.com — the customer PWA. Shopper features only.
 *  Merchant + admin routes redirect back to their proper hosts. */
function AppRoutes() {
  return (
    <>
      {!isComingSoon && <PWAInstallBanner />}
      <Routes>
        <Route path="/" element={isComingSoon ? <ComingSoon /> : <AppShell><Home /></AppShell>} />
        <Route path="/coupons" element={<AppShell><Coupons /></AppShell>} />
        <Route path="/coupon/:id" element={<AppShell><CouponDetail /></AppShell>} />
        <Route path="/stores" element={<AppShell><Stores /></AppShell>} />
        <Route path="/store/:slug" element={<AppShell><StorefrontDetail /></AppShell>} />
        <Route path="/store-legacy/:slug" element={<AppShell><StoreDetail /></AppShell>} />
        <Route path="/brands" element={<AppShell><div className="max-w-6xl mx-auto px-6 py-8"><h1 className="font-display text-3xl text-slate-900 mb-6">Discover brands</h1><BrandsFeed limit={48} /></div></AppShell>} />
        <Route path="/categories" element={<AppShell><Categories /></AppShell>} />
        <Route path="/search" element={<AppShell><Search /></AppShell>} />
        <Route path="/login" element={<AppShell><Login /></AppShell>} />
        <Route path="/phone-login" element={<AppShell><PhoneLogin /></AppShell>} />
        <Route path="/register" element={<AppShell><Register /></AppShell>} />
        <Route path="/terms" element={<AppShell><Terms /></AppShell>} />
        <Route path="/privacy" element={<AppShell><Privacy /></AppShell>} />
        <Route path="/terms/documents/:id" element={<AppShell><AgreementView /></AppShell>} />
        <Route path="/legal/agreements/:id" element={<AppShell><AgreementView /></AppShell>} />
        <Route path="/account/terms" element={<AppShell><AccountLegal /></AppShell>} />
        <Route path="/account/legal" element={<Navigate to="/account/terms" replace />} />
        <Route path="/faq" element={<AppShell><PublicFAQ /></AppShell>} />
        <Route path="/blog" element={<AppShell><Blog /></AppShell>} />
        <Route path="/blog/introducing-starter-and-growth" element={<AppShell><BlogLaunchPost /></AppShell>} />
        <Route path="/forgot-password" element={<AppShell><ForgotPassword /></AppShell>} />
        <Route path="/reset-password" element={<AppShell><ResetPassword /></AppShell>} />
        <Route path="/dashboard" element={
          <ProtectedRoute roles={["user"]}>
            <AppShell><UserDashboard /></AppShell>
          </ProtectedRoute>
        } />
        <Route path="/wallet" element={
          <ProtectedRoute roles={["user", "affiliate"]}>
            <AppShell><UserWallet /></AppShell>
          </ProtectedRoute>
        } />
        <Route path="/rewards" element={
          <ProtectedRoute roles={["user", "affiliate"]}>
            <AppShell><RewardsWallet /></AppShell>
          </ProtectedRoute>
        } />
        <Route path="/affiliate/*" element={
          <ProtectedRoute roles={["user", "affiliate"]}>
            <AppShell><AffiliatePortal /></AppShell>
          </ProtectedRoute>
        } />
        <Route path="/r/aff/:slug" element={<AffiliateRedirect />} />
        <Route path="/go/campaign/:id" element={<GoRedirect />} />

        {/* Cross-host redirects — merchant + admin surfaces belong elsewhere. */}
        <Route path="/pricing" element={<CrossHost to="main" path="/pricing" />} />
        <Route path="/why-cyannect" element={<CrossHost to="main" path="/why-cyannect" />} />
        <Route path="/about" element={<AppShell><About /></AppShell>} />
        <Route path="/merchant/*" element={<CrossHost to="main" path="/merchant" />} />
        <Route path="/admin/*" element={<CrossHost to="admin" path="/admin/login" />} />
        <Route path="/admin/login" element={<CrossHost to="admin" path="/admin/login" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

/** Routes for resellers.cyannect.com — reseller application + catalog + dashboard. */
function ResellerRoutes() {
  return (
    <Routes>
      <Route path="/" element={<AppShell><ResellerPortal /></AppShell>} />
      <Route path="/reseller/*" element={<AppShell><ResellerPortal /></AppShell>} />
      <Route path="/login" element={<AppShell><Login /></AppShell>} />
      <Route path="/register" element={<AppShell><Register /></AppShell>} />
      <Route path="/terms" element={<AppShell><Terms /></AppShell>} />
      <Route path="/privacy" element={<AppShell><Privacy /></AppShell>} />
      <Route path="/terms/documents/:id" element={<AppShell><AgreementView /></AppShell>} />
        <Route path="/legal/agreements/:id" element={<AppShell><AgreementView /></AppShell>} />
      <Route path="/account/terms" element={<AppShell><AccountLegal /></AppShell>} />
        <Route path="/account/legal" element={<Navigate to="/account/terms" replace />} />
      <Route path="/forgot-password" element={<AppShell><ForgotPassword /></AppShell>} />
      <Route path="/reset-password" element={<AppShell><ResetPassword /></AppShell>} />
      {/* Everything else redirects to the main site */}
      <Route path="*" element={<CrossHost to="main" path="/" />} />
    </Routes>
  );
}

/** Routes for cyannect.com — the merchant/brand site. No shopper surfaces. */
function MainRoutes() {
  return (
    <Routes>
      <Route path="/" element={isComingSoon ? <ComingSoon /> : <AppShell><MerchantHome /></AppShell>} />
      <Route path="/shoppers" element={<AppShell><ShoppersLanding /></AppShell>} />
      <Route path="/s/:slug" element={<ShortLinkRedirect />} />
      <Route path="/pricing" element={<AppShell><Pricing /></AppShell>} />
      <Route path="/demo" element={<AppShell><Demo /></AppShell>} />
      <Route path="/demo/cancel" element={<AppShell><DemoCancel /></AppShell>} />
      <Route path="/why-cyannect" element={<AppShell><WhyCyannect /></AppShell>} />
      <Route path="/about" element={<AppShell><About /></AppShell>} />
      <Route path="/api-docs" element={<AppShell><ApiDocs /></AppShell>} />
      <Route path="/developers" element={<AppShell><ApiDocs /></AppShell>} />
      <Route path="/docs/web-sdk" element={<AppShell><WebSdkDocs /></AppShell>} />
      <Route path="/docs/conversion-verification" element={<AppShell><ConversionVerificationDocs /></AppShell>} />
      {/* Public Cyannect referral shortlink — no navbar/footer, just bounces to merchant. */}
      <Route path="/r/:cyRef" element={<ReferralRedirect />} />
      {/* Shopper wallet — cashback view. */}
      <Route path="/wallet/cashback" element={<AppShell><ShopperCashback /></AppShell>} />
      <Route path="/rewards" element={<AppShell><RewardsWallet /></AppShell>} />
      <Route path="/merchant/integrations" element={<AppShell><MerchantIntegrations /></AppShell>} />
      <Route path="/login" element={<AppShell><Login /></AppShell>} />
      <Route path="/phone-login" element={<AppShell><PhoneLogin /></AppShell>} />
      <Route path="/register" element={<AppShell><Register /></AppShell>} />
      <Route path="/terms" element={<AppShell><Terms /></AppShell>} />
      <Route path="/privacy" element={<AppShell><Privacy /></AppShell>} />
      <Route path="/terms/documents/:id" element={<AppShell><AgreementView /></AppShell>} />
        <Route path="/legal/agreements/:id" element={<AppShell><AgreementView /></AppShell>} />
      <Route path="/account/terms" element={<AppShell><AccountLegal /></AppShell>} />
        <Route path="/account/legal" element={<Navigate to="/account/terms" replace />} />
      <Route path="/faq" element={<AppShell><PublicFAQ /></AppShell>} />
      <Route path="/blog" element={<AppShell><Blog /></AppShell>} />
      <Route path="/blog/introducing-starter-and-growth" element={<AppShell><BlogLaunchPost /></AppShell>} />
      <Route path="/forgot-password" element={<AppShell><ForgotPassword /></AppShell>} />
      <Route path="/reset-password" element={<AppShell><ResetPassword /></AppShell>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/reseller" element={<AppShell><ResellerPortal /></AppShell>} />
      <Route path="/reseller/*" element={<AppShell><ResellerPortal /></AppShell>} />

      <Route path="/merchant/*" element={
        <ProtectedRoute roles={["merchant", "admin", "super_admin"]}>
          <AppShell><MerchantPortal /></AppShell>
        </ProtectedRoute>
      } />
      <Route path="/admin/accept-invite" element={<AcceptInvite />} />
      <Route path="/admin/*" element={
        <ProtectedRoute roles={INTERNAL_ROLES}>
          <AppShell><AdminPortal /></AppShell>
        </ProtectedRoute>
      } />

      {/* Shopper surfaces on the main site → send them to the app. */}
      <Route path="/coupons" element={<CrossHost to="app" path="/coupons" />} />
      <Route path="/coupon/:id" element={<CrossHostParam to="app" base="/coupon" />} />
      <Route path="/stores" element={<CrossHost to="app" path="/stores" />} />
      <Route path="/store/:slug" element={<CrossHostParam to="app" base="/store" />} />
      <Route path="/categories" element={<CrossHost to="app" path="/categories" />} />
      <Route path="/search" element={<CrossHost to="app" path="/coupons" />} />
      <Route path="/dashboard" element={<CrossHost to="app" path="/dashboard" />} />
      <Route path="/wallet" element={<CrossHost to="app" path="/wallet" />} />
      <Route path="/affiliate/*" element={<CrossHost to="app" path="/affiliate" />} />
      <Route path="/r/aff/:slug" element={<AffiliateRedirect />} />
      <Route path="/go/campaign/:id" element={<GoRedirect />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const host = getHostClass();
  return (
    <div className={`App App--host-${host}`} data-host={host}>
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ThemeProvider>
            <AuthProvider>
              <Toaster position="top-right" richColors />
              <MobileBootstrap />
              {host === "admin" && <AdminRoutes />}
              {host === "app" && <AppRoutes />}
              {host === "reseller" && <ResellerRoutes />}
              {host === "main" && <MainRoutes />}
              {host !== "admin" && <SupportChatWidget />}
              {host !== "admin" && <AnalyticsConsent />}
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
